import { useEffect, useState } from 'react';
import { tripsApi } from '../../api/trips.api';
import { invitationsApi, type Invitation } from '../../api/invitations.api';
import type { Trip } from '../../types';
import { toast } from '../ui/Toast';
import { isAlreadyGone } from '../../api/client';
import Spinner from '../ui/Spinner';
import { UserAvatar } from '../ui/UserAvatar';
import { tripCoParticipants, tripAccountMembers } from '../../utils/tripMembers';
import PlaceholderRow from './participants/PlaceholderRow';
import AddPlaceholderForm from './participants/AddPlaceholderForm';
import { useTranslation } from 'react-i18next';
import { tripPermissions } from '../../utils/tripPermissions';
import { useAuthStore } from '../../stores/auth.store';

interface Props {
  trip: Trip;
  onUpdate: () => void;
}

export default function ParticipantManager({ trip, onUpdate }: Props) {
  const [email, setEmail]     = useState('');
  const [role, setRole]       = useState<'EDITOR' | 'VIEWER'>('EDITOR');
  const [inviting, setInviting] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const { t, i18n } = useTranslation();
  const { isOwner, canEdit } = tripPermissions(trip);
  const userId = useAuthStore(s => s.user?.id);
  const ownerRow = trip.participants.find(p => p.userId === trip.ownerId);
  const mergeTargets = tripAccountMembers(trip).map(p => ({ userId: p.userId, name: p.user.displayName || p.user.username }));
  // ADR-0029: the server sends the age on the trip's first day, never someone else's date.
  const ageSuffix = (userId: string) => {
    const age = trip.participants.find(p => p.userId === userId)?.ageAtTripStart;
    return age != null ? ` · ${t('trip.participants.age', { count: age })}` : '';
  };
  const nameById = new Map(trip.participants.map(p => [p.userId, p.user.displayName || p.user.username]));

  useEffect(() => {
    if (!isOwner) return;
    invitationsApi.list(trip.id)
      .then(setInvitations)
      .catch(() => { /* silent */ });
  }, [trip.id, isOwner]);

  async function handleInvite() {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return;
    setInviting(true);
    try {
      const invitation = await invitationsApi.create(trip.id, normalized, role, i18n.language);
      setInvitations(prev => [invitation, ...prev.filter(i => !(i.email === invitation.email && i.status === 'PENDING'))]);
      setEmail('');
      toast(t('trip.participants.toast.invited'), 'success');
      onUpdate();
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (code === 'EMAIL_NOT_VERIFIED') {
        toast(t('trip.participants.toast.emailNotVerified'), 'error');
      } else {
        toast(code || t('trip.participants.toast.inviteFailed'), 'error');
      }
    } finally {
      setInviting(false);
    }
  }

  async function handleRevoke(invitationId: string) {
    try {
      await invitationsApi.revoke(trip.id, invitationId);
      setInvitations(prev => prev.map(i => i.id === invitationId ? { ...i, status: 'REVOKED' as const } : i).filter(i => i.id !== invitationId));
      toast(t('trip.participants.toast.revoked'), 'success');
    } catch {
      toast(t('trip.participants.toast.revokeFailed'), 'error');
    }
  }

  async function handleRemove(userId: string) {
    try {
      await tripsApi.removeParticipant(trip.id, userId);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    toast(t('trip.participants.toast.removed'), 'success');
    onUpdate();
  }

  async function handleRoleChange(userId: string, newRole: 'EDITOR' | 'VIEWER') {
    try {
      await tripsApi.updateParticipant(trip.id, userId, newRole);
      toast(t('trip.participants.toast.roleChanged'), 'success');
      onUpdate();
    } catch {
      toast(t('trip.participants.toast.roleChangeFailed'), 'error');
    }
  }

  const roleBadge = (r: string) => {
    const colors: Record<string, string> = {
      OWNER:  'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
      EDITOR: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      VIEWER: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    };
    return <span className={`badge ${colors[r] ?? ''}`}>{t(`trip.participants.roles.${r}` as Parameters<typeof t>[0])}</span>;
  };

  const pendingInvitations = invitations.filter(i => i.status === 'PENDING');

  return (
    <div className="space-y-5">
      {/* Owner. ADR-0033: an agency's trip belongs to its lead traveller, a
          placeholder until they redeem their code — linked like any other. */}
      {ownerRow?.user.isPlaceholder ? (
        <PlaceholderRow
          tripId={trip.id}
          participant={ownerRow}
          isOwner={isOwner}
          canEdit={canEdit}
          mergeTargets={mergeTargets}
          onInvited={inv => setInvitations(prev => [inv, ...prev.filter(i => i.placeholderUserId !== inv.placeholderUserId && i.email !== inv.email)])}
          onRemove={handleRemove}
          onChanged={onUpdate}
          ownsTrip
        />
      ) : (
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <UserAvatar name={trip.owner.displayName || trip.owner.username} avatarUrl={trip.owner.avatarUrl} size={36} />
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {trip.owner.displayName || trip.owner.username}
                {trip.ownerId === userId && <span className="text-slate-400 dark:text-slate-500 font-normal"> {t('trip.participants.you')}</span>}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">@{trip.owner.username}{ageSuffix(trip.ownerId)}</p>
            </div>
          </div>
          {roleBadge('OWNER')}
        </div>
      )}

      {tripCoParticipants(trip).map(p => p.user.isPlaceholder ? (
        <PlaceholderRow
          key={p.id}
          tripId={trip.id}
          participant={p}
          isOwner={isOwner}
          canEdit={canEdit}
          mergeTargets={mergeTargets}
          onInvited={inv => setInvitations(prev => [inv, ...prev.filter(i => i.placeholderUserId !== inv.placeholderUserId && i.email !== inv.email)])}
          onRemove={handleRemove}
          onChanged={onUpdate}
        />
      ) : (
        <div key={p.id} className="flex items-center justify-between gap-2 py-2 border-t border-slate-100 dark:border-slate-700/60">
          <div className="flex items-center gap-3 min-w-0">
            <UserAvatar name={p.user.displayName || p.user.username} avatarUrl={p.user.avatarUrl} size={36} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{p.user.displayName || p.user.username}</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 truncate">@{p.user.username}{ageSuffix(p.userId)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isOwner ? (
              <select
                className="input py-1 text-xs w-auto"
                value={p.role}
                onChange={e => handleRoleChange(p.userId, e.target.value as 'EDITOR' | 'VIEWER')}
              >
                <option value="EDITOR">{t('trip.participants.roleEditor')}</option>
                <option value="VIEWER">{t('trip.participants.roleViewer')}</option>
              </select>
            ) : (
              roleBadge(p.role)
            )}
            {isOwner && (
              <button onClick={() => handleRemove(p.userId)} className="btn-ghost text-red-400 text-xs">
                {t('trip.participants.remove')}
              </button>
            )}
          </div>
        </div>
      ))}

      {isOwner && pendingInvitations.length > 0 && (
        <div className="pt-4 border-t border-slate-100 dark:border-slate-700/60">
          <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-3">
            {t('trip.participants.pending')}
          </p>
          <div className="space-y-2">
            {pendingInvitations.map(inv => (
              <div key={inv.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20">
                <div className="min-w-0 flex-1 mr-3">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                    {inv.email}
                    {inv.placeholderUserId && nameById.has(inv.placeholderUserId) && (
                      <span className="font-normal text-slate-500 dark:text-slate-400"> · {t('trip.participants.placeholder.invitationFor', { name: nameById.get(inv.placeholderUserId) })}</span>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    {t('trip.participants.roleLabel', { role: inv.role === 'EDITOR' ? t('trip.participants.roleEditor') : t('trip.participants.roleViewer') })} {new Date(inv.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleRevoke(inv.id)}
                  className="shrink-0 text-[11px] font-semibold text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                >
                  {t('trip.participants.cancelInvite')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {canEdit && <AddPlaceholderForm tripId={trip.id} onAdded={onUpdate} />}

      {trip.agency && !trip.viewerIsAgency && (
        <p className="pt-4 border-t border-slate-100 dark:border-slate-700/60 text-xs text-slate-500 dark:text-slate-400">
          {t('agency.managesTravellers')}
        </p>
      )}

      {isOwner && (
        <div className="pt-4 border-t border-slate-100 dark:border-slate-700/60">
          <p className="label mb-2">{t('trip.participants.invite')}</p>
          <div className="flex flex-col gap-2">
            <input
              type="email"
              className="input flex-1 min-w-0"
              placeholder="amigo@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleInvite()}
            />
            <div className="flex gap-2">
              <select
                className="input flex-1 min-w-0"
                value={role}
                onChange={e => setRole(e.target.value as 'EDITOR' | 'VIEWER')}
              >
                <option value="EDITOR">{t('trip.participants.roleEditor')}</option>
                <option value="VIEWER">{t('trip.participants.roleViewer')}</option>
              </select>
              <button onClick={handleInvite} className="btn-primary shrink-0" disabled={inviting || !email.trim()}>
                {inviting ? <Spinner size="sm" /> : t('trip.participants.sendInvitation')}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
            {t('trip.participants.hint')}
          </p>
        </div>
      )}

      {!isOwner && (
        <p className="pt-4 border-t border-slate-100 dark:border-slate-700/60 text-[11px] text-slate-500 dark:text-slate-400">
          {t('trip.participants.viewerHint')}
        </p>
      )}
    </div>
  );
}
